import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { BUSINESS_INFO } from '../../mocks/lisaMerkleData';

const CREDENTIALS = [
  { label: 'UCLA Trained', detail: 'Formal design education' },
  { label: 'Celebrity Firm Experience', detail: 'Beverly Hills & LA' },
  { label: 'Elle Décor Published', detail: 'Editorial recognition' },
  { label: 'Architectural Digest', detail: 'Featured design studio' },
];

export default function PhilosophySection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.querySelectorAll('.reveal').forEach((node, i) => {
          setTimeout(() => node.classList.add('revealed'), i * 100);
        });
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section ref={ref} className="bg-white py-28 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-8">

        {/* Main grid */}
        <div className="grid lg:grid-cols-2 gap-0 border border-stone/20">
          {/* Left: Image with overlay block */}
          <div className="reveal relative min-h-[480px] overflow-hidden">
            <img
              src="https://public.readdy.ai/ai/img_res/15efec572bcdf64c5d1c776265da395b.jpg"
              alt="Lisa Merkle Design philosophy — luxury living space"
              className="w-full h-full object-cover object-top absolute inset-0 transition-transform duration-1000 hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-transparent to-transparent" />
            {/* Credential overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <div className="grid grid-cols-2 gap-3">
                {CREDENTIALS.map((c) => (
                  <div key={c.label} className="flex items-start gap-2">
                    <i className="ri-checkbox-circle-line text-stone text-sm mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-cream text-xs font-sans font-medium leading-snug">{c.label}</p>
                      <p className="text-cream/40 text-[10px] font-sans">{c.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="reveal reveal-delay-2 flex flex-col justify-center p-12 lg:p-16 bg-cream-warm">
            <span className="text-[10px] tracking-[0.28em] uppercase text-stone font-sans">Our Approach</span>
            <h2 className="font-display text-4xl md:text-5xl font-light text-charcoal mt-4 mb-8 leading-tight">
              Why Choose<br />
              <em className="not-italic text-stone-dark">Lisa Merkle Design</em>
            </h2>

            <div className="flex flex-col gap-5">
              <div className="reveal reveal-delay-3 flex items-start gap-4">
                <div className="w-px self-stretch bg-stone/40 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg font-medium text-charcoal mb-2">Our Vision</h3>
                  <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                    Exceptional homes should inspire daily life while protecting long-term value. We create timeless interiors through thoughtful design, curated materials, and architectural sensitivity.
                  </p>
                </div>
              </div>
              <div className="reveal reveal-delay-4 flex items-start gap-4">
                <div className="w-px self-stretch bg-stone/40 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg font-medium text-charcoal mb-2">Our Mission</h3>
                  <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                    Delivering interiors that combine refined beauty with intelligent investment decisions. We collaborate with architects, contractors, and artisans to ensure every detail is executed to the highest standard.
                  </p>
                </div>
              </div>
              <div className="reveal reveal-delay-5 flex items-start gap-4">
                <div className="w-px self-stretch bg-stone/40 flex-shrink-0" />
                <div>
                  <h3 className="font-display text-lg font-medium text-charcoal mb-2">Design Philosophy</h3>
                  <p className="text-charcoal/60 text-sm leading-relaxed font-sans">
                    Classical European influence — French, Italian, and Spanish — blended with California sophistication. Proportion, architectural integrity, natural light, and curated materials create interiors that feel balanced, refined, and enduring.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-10">
              <Link
                to="/about"
                className="px-7 py-3 bg-charcoal text-cream text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-charcoal/80 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                Our Story
              </Link>
              <a
                href={BUSINESS_INFO.phoneHref}
                className="px-7 py-3 border border-charcoal/20 text-charcoal text-[10px] tracking-[0.2em] uppercase font-medium font-sans rounded-full hover:bg-charcoal/5 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                {BUSINESS_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
